import React from 'react';
import { Text, Title, Box, Stack, Paper, Table, Button, Alert, Group, Badge, Accordion, Divider } from '@mantine/core';
import { usePACModelConfig, useTriggerPACModelRecalculation } from '../../../hooks/useSettings';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconRefresh, IconCheck, IconX } from '@tabler/icons';
import { PACModelConfig } from '../../../api/settings';

export const PACModelConfigTab: React.FC = () => {
  const { data: pacModelConfig, isLoading, error } = usePACModelConfig();
  const { mutate: triggerRecalculation, isPending: isRecalculating } = useTriggerPACModelRecalculation();

  const handleRecalculation = () => {
    triggerRecalculation(undefined, {
      onSuccess: () => {
        notifications.show({
          title: 'Success',
          message: 'PACModelConfig recalculation initiated successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      },
      onError: (error: any) => {
        notifications.show({
          title: 'Error',
          message: error?.response?.data?.message || 'Failed to trigger recalculation',
          color: 'red',
          icon: <IconX size={16} />,
        });
      },
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const renderObjectProperties = (obj: any, excludeKeys: string[] = []) => {
    return Object.entries(obj)
      .filter(([key]) => !excludeKeys.includes(key))
      .map(([key, value]) => (
        <tr key={key}>
          <td style={{ fontWeight: 500 }}>{key}</td>
          <td style={{ wordBreak: 'break-all', maxWidth: '300px' }}>
            {typeof value === 'object' ? (
              <pre style={{ fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(value, null, 2)}
              </pre>
            ) : (
              formatValue(value)
            )}
          </td>
        </tr>
      ));
  };

  if (isLoading) {
    return (
      <Stack spacing="md">
        <Box>
          <Title order={4}>PAC Model Configuration</Title>
          <Text size="sm" color="dimmed">Configure and manage PAC model settings.</Text>
        </Box>
        <Paper p="lg" withBorder>
          <Text size="sm" color="dimmed" ta="center">Loading...</Text>
        </Paper>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack spacing="md">
        <Box>
          <Title order={4}>PAC Model Configuration</Title>
          <Text size="sm" color="dimmed">Configure and manage PAC model settings.</Text>
        </Box>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load PAC model configuration. Please try again later.
        </Alert>
      </Stack>
    );
  }

  if (!pacModelConfig || (pacModelConfig.message && pacModelConfig.message === "No customization found")) {
    return (
      <Stack spacing="md">
        <Box>
          <Title order={4}>PAC Model Configuration</Title>
          <Text size="sm" color="dimmed">Configure and manage PAC model settings.</Text>
        </Box>
        <Paper p="lg" withBorder style={{ backgroundColor: '#f8f9fa' }}>
          <Text size="sm" color="dimmed" ta="center">
            No PAC model configuration found.
          </Text>
        </Paper>
      </Stack>
    );
  }

  const config = pacModelConfig as PACModelConfig;

  return (
    <Stack spacing="md">
      <Box>
        <Group position='apart' align='flex-start'>
          <Box>
            <Title order={4}>PAC Model Configuration</Title>
            <Text size="sm" color="dimmed">Configure and manage PAC model settings.</Text>
          </Box>
          <Box>
            <Text align='right' size="sm" color="dimmed">
            Last Updated: {formatDate(config.updated_at)}
            </Text>
            <Group mt="xs" spacing="xs">
              <Badge color={config.auto_recalc ? 'green' : 'gray'}>
                Auto Recalc: {config.auto_recalc ? 'Enabled' : 'Disabled'}
              </Badge>
              <Badge color={config.tuning_needed ? 'yellow' : 'green'}>
                Tuning: {config.tuning_needed ? 'Needed' : 'Not Needed'}
              </Badge>
            </Group>
          </Box>
        </Group>
      </Box>

      {/* Configuration Overview */}
      {/* <Paper p="md" withBorder>
        <Stack spacing="sm">
          <Group position="apart">
            <Text weight={500}>Configuration Status</Text>
            <Group spacing="xs">
              <Badge color={config.auto_recalc ? 'green' : 'gray'}>
                Auto Recalc: {config.auto_recalc ? 'Enabled' : 'Disabled'}
              </Badge>
              <Badge color={config.tuning_needed ? 'yellow' : 'green'}>
                Tuning: {config.tuning_needed ? 'Needed' : 'Not Needed'}
              </Badge>
            </Group>
          </Group>
          <Text size="sm" color="dimmed">
            Last Updated: {formatDate(config.updated_at)}
          </Text>
        </Stack>
      </Paper> */}

      {/* Models Information */}
      <Accordion defaultValue="target-model"  variant="contained">
        {config?.target_model?.model_name && <Accordion.Item value="target-model">
          <Accordion.Control>
            <Group>
              <Badge variant="outline" color="blue">Target Model</Badge>
              <Text size="sm">{config.target_model.model_name} (v{config.target_model.model_version})</Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Table striped highlightOnHover withBorder bg="#fff">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {renderObjectProperties(config.target_model)}
              </tbody>
            </Table>
          </Accordion.Panel>
        </Accordion.Item>}

        {config?.last_completed_recalc && <Accordion.Item value="last-completed">
          <Accordion.Control>
            <Group>
              <Badge variant="outline" color="green">Last Completed Recalculation</Badge>
              <Text size="sm">
                Finished: {formatDate(config.last_completed_recalc.finished_at)}
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack spacing="md">
              {/* Model information */}
              <Box>
                <Text weight={500} size="sm" mb="xs">Model Information</Text>
                <Table striped highlightOnHover withBorder bg="#fff">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderObjectProperties(config.last_completed_recalc.model)}
                  </tbody>
                </Table>
              </Box>

              {/* Other recalc properties */}
              {Object.keys(config.last_completed_recalc).filter(key => key !== 'model' && key !== 'finished_at').length > 0 && (
                <Box>
                  <Text weight={500} size="sm" mb="xs">Additional Properties</Text>
                  <Table striped highlightOnHover withBorder bg="#fff">
                    <thead>
                      <tr>
                        <th>Property</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderObjectProperties(config.last_completed_recalc, ['model', 'finished_at'])}
                    </tbody>
                  </Table>
                </Box>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>}
      </Accordion>

      {/* Recalculation Actions */}
      <Paper p="md" withBorder>
        <Stack spacing="sm">
          <Box>
            <Title order={5}>Recalculation Actions</Title>
            <Text size="sm" color="dimmed">
              Trigger a full recalculation of all product areas (Admin only)
            </Text>
          </Box>
          <Group>
            <Button
              leftIcon={<IconRefresh size={16} />}
              onClick={handleRecalculation}
              loading={isRecalculating}
              color="blue"
            >
              {isRecalculating ? 'Initiating Recalculation...' : 'Trigger Recalculation'}
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default PACModelConfigTab; 